import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CustomProvider } from 'rsuite';
import useAuthSession from '../auth/useAuthSession';
import { useMyThemePreference, useUpdateMyThemePreference } from '../hooks/useQueries';

// App-wide light/dark theme. rsuite's CustomProvider applies the `rs-theme-<theme>`
// class to <body>, which drives all rsuite components.
//
// Persistence is server-side (per user, cross-device) via /api/users/me/preferences.
// localStorage is kept only as a fast cache so the correct theme paints immediately on
// load instead of flashing light while the API request is in flight.

const ThemeContext = createContext({
  theme: 'light',
  isDark: false,
  toggleTheme: () => {},
  setTheme: () => {},
});

export const useTheme = () => useContext(ThemeContext);

const STORAGE_PREFIX = 'ppc.theme';
const VALID_THEMES = new Set(['light', 'dark']);

const storageKeyForUser = (userId) => `${STORAGE_PREFIX}.${userId || 'anonymous'}`;

const readStoredTheme = (userId) => {
  try {
    const value = window.localStorage.getItem(storageKeyForUser(userId));
    return VALID_THEMES.has(value) ? value : null;
  } catch {
    return null;
  }
};

const writeStoredTheme = (userId, theme) => {
  try {
    window.localStorage.setItem(storageKeyForUser(userId), theme);
  } catch {
    // ignore storage failures (private mode, quota, etc.)
  }
};

export default function ThemeProvider({ children }) {
  const { userId, isSignedIn } = useAuthSession();

  const [theme, setThemeState] = useState(() => readStoredTheme(userId) || 'light');

  // Authoritative preference from the server (follows the account across devices).
  const { data: serverPref } = useMyThemePreference(userId, { enabled: Boolean(isSignedIn) });
  const updatePref = useUpdateMyThemePreference(userId);

  // Repaint from the cache immediately when the signed-in user resolves.
  useEffect(() => {
    const stored = readStoredTheme(userId);
    if (stored && stored !== theme) setThemeState(stored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Reconcile with the server value once it loads. If the server has a saved theme it
  // wins (that's how another device's choice reaches this one); if it's null (unset) we
  // keep whatever is local. After a local toggle the mutation updates this cache to the
  // same value, so this effect is a no-op then.
  useEffect(() => {
    const serverTheme = serverPref?.theme;
    if (VALID_THEMES.has(serverTheme) && serverTheme !== theme) {
      setThemeState(serverTheme);
      writeStoredTheme(userId, serverTheme);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverPref?.theme]);

  const setTheme = useCallback(
    (next) => {
      if (!VALID_THEMES.has(next)) return;
      setThemeState(next);
      writeStoredTheme(userId, next);
      if (isSignedIn) {
        // Persist server-side; if it fails (offline, migration not run) localStorage
        // still holds the choice, so the UI stays correct on this device.
        updatePref.mutate(next);
      }
    },
    [userId, isSignedIn, updatePref]
  );

  // The shared NSI components (dialogs, tables) take their dark palette from this class, as
  // rsuite takes its own from the rs-theme-dark class CustomProvider sets.
  useEffect(() => {
    document.body.classList.toggle('nsi-theme-dark', theme === 'dark');
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  const value = useMemo(
    () => ({ theme, isDark: theme === 'dark', toggleTheme, setTheme }),
    [theme, toggleTheme, setTheme]
  );

  return (
    <ThemeContext.Provider value={value}>
      <CustomProvider theme={theme}>{children}</CustomProvider>
    </ThemeContext.Provider>
  );
}
